import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Spinner, Tab, Tabs, Form, Button, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { API_BASE_URL, ENDPOINTS } from '../config';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet-async';

const Profile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, token, isAuthenticated } = useAuth();
  const { formatPrice } = useCurrency();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchOrders();
    }
  }, [isAuthenticated, token]);

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.GET_CART_REQUESTS}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Sort by date desc
        const sortedData = Array.isArray(data) ? data.sort((a: any, b: any) => 
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
        ) : [];
        setOrders(sortedData);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const getStatusBadge = (statusId: number, statusName: string) => {
    let variant = 'secondary';
    switch (statusId) {
      case 1: variant = 'warning'; break; // Pending
      case 2: variant = 'info'; break;    // Processing
      case 3: variant = 'primary'; break; // Shipped
      case 4: variant = 'success'; break; // Delivered
      case 5: variant = 'danger'; break;  // Cancelled
      default: variant = 'secondary';
    }
    return <Badge bg={variant} className="px-3 py-2 rounded-pill">{statusName}</Badge>;
  };

  if (!isAuthenticated) {
    return (
        <div className="profile-page bg-light min-vh-100">
            <Navbar />
            <Container className="py-5 text-center">
                <div className="py-5">
                    <h3>{t('loginRequired')}</h3>
                    <p>{t('loginToViewProfile')}</p>
                    <Button variant="primary" href="/login">{t('login')}</Button>
                </div>
            </Container>
            <Footer />
        </div>
    );
  }

  return (
    <div className="profile-page bg-light min-vh-100">
      <Helmet>
        <title>{t('myProfile')} - MIC Green Store</title>
      </Helmet>

      <Navbar />

      <Container className="py-5">
        <div className="d-flex align-items-center mb-4">
            <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '60px', height: '60px', fontSize: '1.5rem'}}>
                <i className="fas fa-user"></i>
            </div>
            <div>
                <h2 className="fw-bold mb-0">{user?.firstName} {user?.lastName}</h2>
                <p className="text-muted mb-0">{user?.email}</p>
            </div>
        </div>

        <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
            <Card.Header className="bg-white p-0 border-bottom">
                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k || 'orders')}
                    className="profile-tabs border-0"
                    fill
                >
                    <Tab eventKey="orders" title={
                        <div className="py-3">
                            <i className="fas fa-shopping-bag me-2"></i>
                            {t('myOrders') || 'My Orders'}
                        </div>
                    }>
                    </Tab>
                    <Tab eventKey="info" title={
                         <div className="py-3">
                            <i className="fas fa-user-cog me-2"></i>
                            {t('accountSettings') || 'Account Settings'}
                        </div>
                    }>
                    </Tab>
                </Tabs>
            </Card.Header>
            <Card.Body className="p-4">
                {activeTab === 'orders' && (
                    <div className="orders-section">
                        {loadingOrders ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="success" />
                            </div>
                        ) : orders.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="mb-3 text-muted">
                                    <i className="fas fa-box-open fa-3x"></i>
                                </div>
                                <h5>{t('noOrdersYet') || 'No orders yet'}</h5>
                                <Button variant="outline-success" className="mt-3" href="/">
                                    {t('startShopping') || 'Start Shopping'}
                                </Button>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover className="align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0 py-3 ps-4">#</th>
                                            <th className="border-0 py-3">{t('date') || 'Date'}</th>
                                            <th className="border-0 py-3">{t('total') || 'Total'}</th>
                                            <th className="border-0 py-3">{t('items') || 'Items'}</th>
                                            <th className="border-0 py-3">{t('status') || 'Status'}</th>
                                            <th className="border-0 py-3 text-end pe-4">{t('actions') || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orders.map((order, idx) => (
                                            <tr key={order.cartRequestId}>
                                                <td className="ps-4 fw-bold text-muted">#{order.cartRequestId}</td>
                                                <td>
                                                    <div className="d-flex flex-column">
                                                        <span className="fw-medium">{new Date(order.createdDate).toLocaleDateString()}</span>
                                                        <span className="small text-muted">{new Date(order.createdDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                    </div>
                                                </td>
                                                <td className="fw-bold text-success">
                                                    {formatPrice(order.totalWithFee || order.totalAmount, 'USD')}
                                                </td>
                                                <td>
                                                    <Badge bg="light" text="dark" className="border">
                                                        {order.cartItems?.length || 0} {t('items')}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {getStatusBadge(order.statusId, order.statusName)}
                                                </td>
                                                <td className="text-end pe-4">
                                                    <Button variant="link" className="text-decoration-none p-0 text-secondary">
                                                        <i className="fas fa-eye"></i>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'info' && (
                    <div className="account-info-section">
                        <Row className="g-4">
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted small">{t('fullName')}</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={`${user?.firstName} ${user?.lastName}`} 
                                        disabled 
                                        className="bg-light"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted small">{t('email')}</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={user?.email || ''} 
                                        disabled 
                                        className="bg-light"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted small">{t('mobileNumber')}</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={user?.mobileNumber || ''} 
                                        disabled 
                                        className="bg-light"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Alert variant="info" className="mt-3 small">
                            <i className="fas fa-info-circle me-2"></i>
                            {t('editProfileNote') || 'To edit your profile information, please contact support.'}
                        </Alert>
                    </div>
                )}
            </Card.Body>
        </Card>
      </Container>
      <Footer />
    </div>
  );
};

export default Profile;
